/**
 * usePublicationsWithAuthors Hook
 *
 * Reusable React Query hook for fetching publications with author data.
 * Uses two-step fetch pattern to bypass RLS restrictions on users table:
 * 1. Fetch publications from content_submissions (without users join)
 * 2. Fetch author details from public content_authors source
 * 3. Batch fetch localized metadata from localization_metadata
 * 4. Map authors and localized data onto publications in-memory
 *
 * This ensures author avatars and names display correctly for anonymous users.
 * Follows the metadata-first localization pattern - no runtime translation.
 */

import { useQuery } from '@tanstack/react-query';
import { db, type FilterOperator } from '@/lib/supabaseQuery';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';
import { logger } from '@/utils/logger';
import { usePrimaryLocale } from './usePrimaryLocale';
import { useLocale } from '@/contexts/LocaleContext';
import { ORPHAN_SENTINEL_UUID } from '@/constants/config';
import { fetchPublicationPreviews, needsPreview } from '@/hooks/usePublicationPreviews';

export interface PublicationAuthor {
  id?: string;
  display_name: string | null;
  author_name?: string | null;
  business?: string | null;
  vanity_url: string | null;
  image: string | null;
  country: string | null;
  about?: string | null;
  profile_listed?: boolean;
}

export interface PublicationWithAuthor {
  id: string;
  slug: string;
  slugEn?: string;
  title: string;
  excerpt: string | null;
  body: string | null;
  featured_image_url: string | null;
  published_at: string | null;
  author_id: string | null;
  location_id: string | null;
  users: PublicationAuthor | null;
  topicIds: string[];
  localizedTitle?: string;
  localizedExcerpt?: string;
  localizedSlug?: string;
  authorProfileListed?: boolean;
}

export interface UsePublicationsWithAuthorsOptions {
  locationId?: string | null;
  limit?: number;
  diversityMode?: 'author' | 'location' | 'none';
  allowGlobalFallback?: boolean;
  enabled?: boolean;
}

interface LocalPublicationRow {
  id: string;
  slug: string;
  slug_en: string | null;
  title: string;
  excerpt: string | null;
  featured_image_url: string | null;
  published_at: string | null;
  author_id: string | null;
  location_id: string | null;
  topic_ids: string[] | null;
}

interface RawPublication {
  id: string;
  slug: string;
  slug_en: string | null;
  title: string;
  excerpt: string | null;
  body: string | null;
  featured_image_url: string | null;
  published_at: string | null;
  author_id: string | null;
  location_id: string | null;
  topic_ids: string[] | null;
}

async function fetchAuthorDetails(authorIds: string[]): Promise<Map<string, PublicationAuthor>> {
  if (authorIds.length === 0) return new Map();

  try {
    const data = await db.fetchAll<{
      id: string;
      display_name: string | null;
      author_name: string | null;
      vanity_url: string | null;
      image: string | null;
      country: string | null;
      about: string | null;
      profile_listed: boolean | null;
    }>(
      'content_authors',
      'id, display_name, author_name, vanity_url, image, country, about, profile_listed',
      {
        filters: [{ column: 'id', operator: 'in', value: authorIds }],
      }
    );

    const authorMap = new Map<string, PublicationAuthor>();
    data.forEach((author) => {
      authorMap.set(author.id, {
        id: author.id,
        display_name: author.display_name,
        author_name: author.author_name,
        vanity_url: author.vanity_url,
        image: author.image,
        country: author.country,
        about: author.about,
        profile_listed: author.profile_listed ?? undefined,
      });
    });

    return authorMap;
  } catch (error) {
    logger.error('Failed to fetch author details', { error });
    return new Map();
  }
}

async function fetchLocalizedMetadata(
  publicationIds: string[],
  contentLocale: string
): Promise<Map<string, { title?: string; excerpt?: string; slug?: string }>> {
  if (publicationIds.length === 0 || contentLocale === 'en') {
    return new Map();
  }

  const metadataData = await db.fetchAll<{
    source_id: string;
    translated_name: string | null;
    translated_body_short: string | null;
    translated_slug: string | null;
  }>(
    'localization_metadata',
    'source_id, translated_name, translated_body_short, translated_slug',
    {
      filters: [
        { column: 'source_type', operator: 'eq', value: 'publication' },
        { column: 'locale', operator: 'eq', value: contentLocale },
        { column: 'source_id', operator: 'in', value: publicationIds },
      ],
    }
  );

  const map = new Map<string, { title?: string; excerpt?: string; slug?: string }>();
  metadataData?.forEach((metadata) => {
    map.set(metadata.source_id, {
      title: metadata.translated_name || undefined,
      excerpt: metadata.translated_body_short || undefined,
      slug: metadata.translated_slug || undefined,
    });
  });

  return map;
}

export function applyDiversityFilter(
  publications: PublicationWithAuthor[],
  mode: 'author' | 'location' | 'none',
  limit: number
): PublicationWithAuthor[] {
  if (mode === 'none') return publications.slice(0, limit);

  const seen = new Set<string>();
  const diverse: PublicationWithAuthor[] = [];
  const remaining: PublicationWithAuthor[] = [];

  for (const publication of publications) {
    const key = mode === 'author' ? publication.author_id : publication.location_id;

    if (key && !seen.has(key)) {
      seen.add(key);
      diverse.push(publication);
    } else {
      remaining.push(publication);
    }
  }

  const seenAuthors = new Set<string>();
  if (mode === 'location') {
    for (const publication of diverse) {
      if (publication.users?.vanity_url) {
        seenAuthors.add(publication.users.vanity_url);
      }
    }
  }

  const result = [...diverse];
  for (const publication of remaining) {
    if (result.length >= limit) break;

    if (mode === 'location' && publication.users?.vanity_url) {
      if (seenAuthors.has(publication.users.vanity_url)) continue;
      seenAuthors.add(publication.users.vanity_url);
    }

    result.push(publication);
  }

  if (result.length < limit) {
    for (const publication of remaining) {
      if (result.length >= limit) break;
      if (!result.includes(publication)) {
        result.push(publication);
      }
    }
  }

  return result.slice(0, limit);
}

function mapPublicationRow(publication: LocalPublicationRow): RawPublication {
  return {
    id: publication.id,
    slug: publication.slug,
    slug_en: publication.slug_en,
    title: publication.title,
    excerpt: publication.excerpt,
    body: publication.excerpt,
    featured_image_url: publication.featured_image_url,
    published_at: publication.published_at,
    author_id: publication.author_id,
    location_id: publication.location_id,
    topic_ids: publication.topic_ids,
  };
}

// SECURITY: content_html is no longer selectable from the client — paywalled
// bodies must not ship inside list payloads. Cards fall back to the stored
// excerpt only; the full body is served by get_publication_body().
const PUBLICATION_SELECT = 'id, slug, slug_en, title, excerpt, featured_image_url, published_at, author_id, location_id, topic_ids';


async function fetchPublishedPublications(locationId: string | null, limit: number): Promise<RawPublication[]> {
  if (locationId) {
    const rows = await db.fetchTop<LocalPublicationRow>('content_submissions', PUBLICATION_SELECT, limit, {
      filters: [
        { column: 'editorial_status', operator: 'eq', value: 'published' },
        { column: 'location_id', operator: 'eq', value: locationId },
      ],
      orderBy: { column: 'published_at', ascending: false },
    });

    return rows.map(mapPublicationRow);
  }

  const rows = await db.fetchTop<LocalPublicationRow>('content_submissions', PUBLICATION_SELECT, limit, {
    filters: [
      { column: 'editorial_status', operator: 'eq', value: 'published' },
      { column: 'location_id', operator: 'neq', value: null },
    ],
    orderBy: { column: 'published_at', ascending: false },
  });

  return rows.map(mapPublicationRow);
}

/**
 * Hydrate raw publications with author details and localized metadata.
 */
async function hydratePublications(
  publications: RawPublication[],
  contentLocale: string
): Promise<PublicationWithAuthor[]> {
  const authorIds = [...new Set(
    publications.map((a) => a.author_id).filter((id): id is string => Boolean(id))
  )];
  const publicationIds = publications.map((a) => a.id);

  const [authorMap, localizedMap] = await Promise.all([
    fetchAuthorDetails(authorIds),
    fetchLocalizedMetadata(publicationIds, contentLocale),
  ]);

  return publications.map((publication) => {
    const localized = localizedMap.get(publication.id);
    const author = publication.author_id ? authorMap.get(publication.author_id) || null : null;

    return {
      ...publication,
      slugEn: publication.slug_en || undefined,
      users: author,
      topicIds: publication.topic_ids ?? [],
      localizedTitle: localized?.title,
      localizedExcerpt: localized?.excerpt,
      localizedSlug: localized?.slug,
      authorProfileListed: author?.profile_listed ?? true,
    };
  });
}

async function fetchPublicationsWithAuthors(
  locationId: string | null,
  limit: number,
  diversityMode: 'author' | 'location' | 'none',
  contentLocale: string,
  allowGlobalFallback: boolean
): Promise<PublicationWithAuthor[]> {
  const fetchLimit = diversityMode === 'none' ? limit : Math.max(limit * 5, 50);

  let publications = await fetchPublishedPublications(locationId, fetchLimit);
  let usedFallback = false;

  if (locationId && publications.length === 0 && allowGlobalFallback) {
    logger.info('No publications for location, falling back to recent global publications', { locationId });
    publications = await fetchPublishedPublications(null, fetchLimit);
    usedFallback = true;
  }

  // Temporary excerpt fallback: body-derived preview for rows without one.
  const previewIds = publications.filter((p) => needsPreview(p.excerpt)).map((p) => p.id);
  const previews = await fetchPublicationPreviews(previewIds);
  for (const p of publications) {
    if (needsPreview(p.excerpt)) {
      const preview = previews.get(p.id);
      if (preview) {
        p.excerpt = preview;
        p.body = preview;
      }
    }
  }

  const hydrated = await hydratePublications(publications, contentLocale);

  const appliedDiversityMode = usedFallback && diversityMode === 'none'
    ? 'location'
    : diversityMode;

  return applyDiversityFilter(hydrated, appliedDiversityMode, limit);
}

/**
 * Fetch the latest publication from the top-ranked expert per location.
 * Used for international homepage "Experts" pill header.
 */
async function fetchExpertFeaturedPublications(
  contentLocale: string,
  limit: number
): Promise<PublicationWithAuthor[]> {
  const { data: rpcData, error } = await supabase
    .rpc('get_expert_featured_publications', { p_limit: limit });

  if (error || !rpcData || rpcData.length === 0) {
    if (error) logger.error('Failed to fetch expert featured publications', { error });
    return [];
  }

  const publicationIds = rpcData.map((r: { publication_id: string }) => r.publication_id);

  const rows = await db.fetchAll<LocalPublicationRow>('content_submissions', PUBLICATION_SELECT, {
    filters: [{ column: 'id', operator: 'in', value: publicationIds }],
  });

  const publications = rows.map(mapPublicationRow);

  // Preserve RPC order (by global_rank ASC)
  const orderMap = new Map<string, number>();
  rpcData.forEach((r: { publication_id: string }, idx: number) => {
    orderMap.set(r.publication_id, idx);
  });
  publications.sort((a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999));

  return hydratePublications(publications, contentLocale);
}

/**
 * Fetch recent human publications (excluding sentinel), optionally filtered by location.
 */
async function fetchRecentHumanPublications(
  contentLocale: string,
  limit: number,
  locationId: string | null
): Promise<PublicationWithAuthor[]> {
  const filters: Array<{ column: string; operator: FilterOperator; value: unknown }> = [
    { column: 'editorial_status', operator: 'eq', value: 'published' },
    { column: 'author_id', operator: 'neq', value: ORPHAN_SENTINEL_UUID },
  ];

  if (locationId) {
    filters.push({ column: 'location_id', operator: 'eq', value: locationId });
  }

  const rows = await db.fetchTop<LocalPublicationRow>('content_submissions', PUBLICATION_SELECT, limit, {
    filters,
    orderBy: { column: 'published_at', ascending: false },
  });

  const publications = rows.map(mapPublicationRow);
  return hydratePublications(publications, contentLocale);
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function usePublicationsWithAuthors(options: UsePublicationsWithAuthorsOptions = {}) {
  const {
    locationId = null,
    limit = 6,
    diversityMode = 'location',
    allowGlobalFallback = true,
    enabled = true,
  } = options;

  const { settings } = useLocale();
  const { resolveToPrimary } = usePrimaryLocale();
  const contentLocale = resolveToPrimary(settings.locale);

  return useQuery({
    queryKey: [...queryKeys.publications.withAuthors({ locationId, limit, diversityMode }), contentLocale, allowGlobalFallback],
    queryFn: () => fetchPublicationsWithAuthors(locationId, limit, diversityMode, contentLocale, allowGlobalFallback),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Fetch the latest publication from the top-ranked expert per location.
 * For international homepage "Experts" pill — featured header set.
 */
export function useExpertFeaturedPublications(options: {
  limit?: number;
  enabled?: boolean;
} = {}) {
  const { limit = 50, enabled = true } = options;
  const { settings } = useLocale();
  const { resolveToPrimary } = usePrimaryLocale();
  const contentLocale = resolveToPrimary(settings.locale);

  return useQuery({
    queryKey: ['expert-featured-publications', limit, contentLocale],
    queryFn: () => fetchExpertFeaturedPublications(contentLocale, limit),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Fetch recent human (non-sentinel) publications, optionally filtered by location.
 * For "Experts" pill backfill content.
 */
export function useRecentHumanPublications(options: {
  limit?: number;
  locationId?: string | null;
  enabled?: boolean;
} = {}) {
  const { limit = 50, locationId = null, enabled = true } = options;
  const { settings } = useLocale();
  const { resolveToPrimary } = usePrimaryLocale();
  const contentLocale = resolveToPrimary(settings.locale);

  return useQuery({
    queryKey: ['recent-human-publications', limit, locationId, contentLocale],
    queryFn: () => fetchRecentHumanPublications(contentLocale, limit, locationId),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
